// Trident — second decoy. The magician opens this note, types three
// numbered lines (1. / 2. / 3.), then hands the phone to the spectator.
// The spectator writes one word per line in any order. When the
// magician taps the ••• reveal button, the note discloses the three
// words in the *chronological* order the spectator wrote them — not
// the visual top-to-bottom order.
//
// Detection model: we track, per line, the first keystroke that lands
// any non-whitespace character on it. Subsequent edits / backspaces
// don't change the order — the spectator is locked in on first touch.
//
// Reveal style is the global Reveal Style (peek / scroll / whisper),
// matching the compose multi-question peek the magician already knows.

import { STORAGE } from "./storage";

export const DEFAULT_TRIDENT_TEMPLATE = "1) (a)  ·  2) (b)  ·  3) (c)";

/** Read the full Trident configuration in one shot. */
export function getTridentConfig() {
  return {
    enabled: localStorage.getItem(STORAGE.TRIDENT_ENABLED) === "1",
    template:
      localStorage.getItem(STORAGE.TRIDENT_TEMPLATE) || DEFAULT_TRIDENT_TEMPLATE,
  };
}

export function saveTridentEnabled(v) {
  localStorage.setItem(STORAGE.TRIDENT_ENABLED, v ? "1" : "0");
}
export function saveTridentTemplate(s) {
  localStorage.setItem(STORAGE.TRIDENT_TEMPLATE, String(s ?? ""));
}

/**
 * Parse a textarea body looking for three numbered lines (1. / 2. / 3.)
 * and return the word/phrase the spectator wrote on each line.
 *
 * Each line's leading marker can be `.`, `)`, `:` or just whitespace.
 * Everything after the marker and whitespace is captured (trimmed).
 *
 * Returns `{ 1: "apple", 2: "river", 3: "moon" }` with empty strings
 * for lines the spectator hasn't written on yet.
 */
export function parseTridentWordLines(text) {
  const lines = String(text || "").split(/\r?\n/);
  const out = { 1: "", 2: "", 3: "" };
  for (const line of lines) {
    const m = /^\s*([123])[.):\s]\s*(.*)$/.exec(line);
    if (!m) continue;
    const pos = parseInt(m[1], 10);
    if (out[pos]) continue; // first occurrence wins
    const rest = m[2].trim();
    if (rest) out[pos] = rest;
  }
  return out;
}

/**
 * Substitute `(a)`, `(b)`, `(c)` placeholders in `template` with the three
 * chronologically-ordered spectator words. Missing entries become "—".
 * If the template has no placeholders, the words are joined as a fallback.
 */
export function buildTridentReveal(template, orderedWords) {
  const a = (orderedWords[0] || "—").trim() || "—";
  const b = (orderedWords[1] || "—").trim() || "—";
  const c = (orderedWords[2] || "—").trim() || "—";
  const tpl = String(template || "").trim();
  if (!tpl) return `1) ${a}  ·  2) ${b}  ·  3) ${c}`;
  if (/\((a|b|c)\)/i.test(tpl)) {
    return tpl
      .replace(/\(a\)/gi, a)
      .replace(/\(b\)/gi, b)
      .replace(/\(c\)/gi, c);
  }
  return `${tpl} ${a} · ${b} · ${c}`;
}
